// F3 debug overlay (M24). The text HUD is a pure function of a per-frame snapshot
// (coords, chunk math, facing, biome, time, counts) so the formatting is unit-
// tested without a game; boot feeds it live and toggles the DOM <pre>. `boxEdges`
// builds the 12 wireframe edges of an AABB for the F3+G chunk-border and F3+B
// hitbox line overlays.

export interface DebugSnapshot {
  fps: number;
  x: number;
  y: number;
  z: number;
  yaw: number; // radians
  pitch: number; // radians
  facing: string; // 'north' | 'east' | 'south' | 'west'
  dimension: string;
  biome: string;
  dayTime: number;
  entities: number;
  section: number; // WORLD.SECTION (horizontal chunk size)
  renderDistance: number;
  /** Adaptive cap actually streamed (≤ renderDistance); shown when it differs. */
  renderCap?: number;
}

/** Minecraft's "towards ±axis" note for a cardinal facing. */
const FACING_AXIS: Record<string, string> = {
  north: 'towards -Z',
  south: 'towards +Z',
  west: 'towards -X',
  east: 'towards +X',
};

const mod = (n: number, m: number): number => ((n % m) + m) % m;
const deg = (r: number): number => (r * 180) / Math.PI;
/** Wrap to (-180, 180], like the yaw readout. */
const wrap180 = (d: number): number => {
  let x = d % 360;
  if (x > 180) x -= 360;
  if (x <= -180) x += 360;
  return x;
};

/** Whether a (possibly out-of-range) day time reads as Day or Night. */
export function dayPhase(dayTime: number): string {
  return mod(dayTime, 24_000) < 12_000 ? 'Day' : 'Night';
}

/** The F3 text lines for a frame snapshot. */
export function debugLines(s: DebugSnapshot): string[] {
  const bx = Math.floor(s.x), by = Math.floor(s.y), bz = Math.floor(s.z);
  const cx = Math.floor(bx / s.section), cz = Math.floor(bz / s.section);
  const lx = mod(bx, s.section), lz = mod(bz, s.section);
  return [
    `${s.fps} fps`,
    `XYZ: ${s.x.toFixed(3)} / ${s.y.toFixed(3)} / ${s.z.toFixed(3)}`,
    `Block: ${bx} ${by} ${bz}`,
    `Chunk: ${lx} ${lz} in ${cx} ${cz}`,
    `Facing: ${s.facing} (${FACING_AXIS[s.facing] ?? '?'}) yaw ${wrap180(deg(s.yaw)).toFixed(1)} pitch ${(-deg(s.pitch)).toFixed(1)}`,
    `Dimension: ${s.dimension}`,
    `Biome: ${s.biome}`,
    `Time: ${Math.floor(s.dayTime)} (${dayPhase(s.dayTime)})`,
    `Entities: ${s.entities}`,
    // Show the adaptive cap alongside the setting when the client has pulled it in.
    s.renderCap !== undefined && s.renderCap < s.renderDistance
      ? `Render distance: ${s.renderDistance} (auto ${s.renderCap})`
      : `Render distance: ${s.renderDistance}`,
  ];
}

/** The 12 edges of the AABB [min..max], as flat XYZ vertex pairs (72 numbers) —
 *  appended into a LineSegments buffer for the chunk-border / hitbox overlays. */
export function boxEdges(
  x0: number, y0: number, z0: number,
  x1: number, y1: number, z1: number,
  out: number[] = [],
): number[] {
  const c: [number, number, number][] = [
    [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], // bottom ring
    [x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1], // top ring
  ];
  const edges: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0], // bottom
    [4, 5], [5, 6], [6, 7], [7, 4], // top
    [0, 4], [1, 5], [2, 6], [3, 7], // verticals
  ];
  for (const [a, b] of edges) {
    const va = c[a]!, vb = c[b]!;
    out.push(va[0], va[1], va[2], vb[0], vb[1], vb[2]);
  }
  return out;
}
